import { Box, Typography } from '@mui/material';
import { isNumber } from 'lodash';
import { MatchTilePlayerContext } from 'modules/matches/providers/MatchTilePlayerProvider';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { MatchFeaturedButton } from 'shared/components/FeaturedButton';
import { ChessMatchTypeFormat } from 'shared/components/Format/ChessMatchTypeFormat';
import { useStyles } from './MatchTile.styles';
import { MatchTilePlayer } from 'modules/matches/enums';
import { UserAvatar } from 'shared/components/UserAvatar';
import { EvIoGameSetupSettings, Identifier, MatchStatus, PlatformType } from '@okletsplay/core';
import { DateTimeFormat } from 'shared/components/Format/DateTimeFormat';
import { DateFormat } from 'app/enums';
import { MatchTileStatusText } from './MatchTileStatusText';
import { useMatchStatus } from 'modules/matches/hooks';
import pluralize from 'pluralize';
import { MatchEntity, UserEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import { GameActivityPaymentProvider } from 'modules/game-payments/providers';
import { GameActivityType } from 'shared/enums';
import {
  EvIoMatchTypeFormat,
  EvIoMatchTypeFormatVariant,
} from 'shared/components/Format/EvIoMatchTypeFormat';

interface MatchTileProps {
  match: MatchEntity;
  hideDates?: boolean;
  onClick: (id: Identifier) => void;
}

export const MatchTile: FC<MatchTileProps> = ({
  match,
  onClick = () => null,
  hideDates = false,
}) => {
  const { classes } = useStyles();

  const { tilePlayer } = useContext(MatchTilePlayerContext);
  const { currentUserPlayer, opponentPlayer, ownerPlayer } = useMatchStatus(match);
  const player = useMemo(() => {
    switch (tilePlayer) {
      case MatchTilePlayer.Opponent:
        return opponentPlayer ?? ownerPlayer;
      case MatchTilePlayer.Owner:
        return ownerPlayer;
      case MatchTilePlayer.CurrentUser:
      default:
        return currentUserPlayer ?? ownerPlayer;
    }
  }, [currentUserPlayer, opponentPlayer, ownerPlayer, tilePlayer]);
  const matchDescription = useMemo(() => {
    const platformType = match?.game?.platformType;

    if (
      [PlatformType.Lichess, PlatformType.Lidraughts].includes(platformType) &&
      isNumber(match.clockLimit) &&
      isNumber(match.clockIncrement)
    ) {
      return (
        <>
          Standard | 1v1 |{' '}
          <ChessMatchTypeFormat limit={match.clockLimit} increment={match.clockIncrement} />
        </>
      );
    }

    if (platformType === PlatformType.EvIo) {
      return (
        <EvIoMatchTypeFormat
          variant={EvIoMatchTypeFormatVariant.OneLine}
          settings={match.matchSetupSettings as unknown as EvIoGameSetupSettings}
          gameId={match?.game?.id}
        />
      );
    }

    return 'Standard | 1v1';
  }, [
    match.clockIncrement,
    match.clockLimit,
    match?.game?.id,
    match?.game?.platformType,
    match.matchSetupSettings,
  ]);

  const { joinRequestsCount, isCurrentUserOwner } = useMatchStatus(match);
  const isPendingLabelVisible =
    isCurrentUserOwner && Boolean(joinRequestsCount) && match.status === MatchStatus.Pending;

  const onClickHandler = useCallback(() => {
    if (!match) {
      return;
    }

    onClick(match.id);
  }, [match, onClick]);

  const matchStatus = useMemo(() => {
    const isLive = match.status === MatchStatus.Live;
    const openStatuses = [
      MatchStatus.Created,
      MatchStatus.Pending,
      MatchStatus.Live,
      MatchStatus.Lobby,
    ];
    const isOpen = openStatuses.includes(match.status);

    if (hideDates && isOpen) {
      return (
        <Typography variant="subtitle2" component="span" className={classes.openMatchLabel}>
          Open
        </Typography>
      );
    }

    if (hideDates && !isOpen) {
      return (
        <Typography variant="subtitle2" className={classes.finishedMatchLabel}>
          Finished
        </Typography>
      );
    }

    if (!hideDates && isLive) {
      return <DateTimeFormat date={match.startedAt} format={DateFormat.DdMmmHhMm} />;
    }

    return <DateTimeFormat date={match.createdAt} format={DateFormat.DdMmmHhMm} />;
  }, [match.status, match.createdAt, match.startedAt, hideDates, classes]);

  const content = (
    <Box className={classNames(classes.root, classes[match.status])} onClick={onClickHandler}>
      <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="row" alignItems="center">
            <UserAvatar user={player?.user as UserEntity} />
            <Typography className={classNames(classes.userName, 'text-overflow')}>
              {player?.user?.userName}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box pr={1}>
              <MatchFeaturedButton value={match.isFeatured} entity={match} />
            </Box>
            <img className={classes.gameIcon} src={match?.game?.icon} />
          </Box>
        </Box>
        <Typography variant="subtitle2" className={classNames(classes.matchType, 'text-overflow')}>
          {matchDescription}
        </Typography>
        <Box className={classes.statusBlock}>
          <MatchTileStatusText match={match} />
          {isPendingLabelVisible && (
            <Typography variant="subtitle2">
              {pluralize('pending request', joinRequestsCount, true)}
            </Typography>
          )}
          <Typography variant="subtitle2" className="text-overflow">
            {matchStatus}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <GameActivityPaymentProvider gameActivity={match} activityType={GameActivityType.Match}>
      {content}
    </GameActivityPaymentProvider>
  );
};
